import {
  Component,
  OnInit,
  OnDestroy,
  AfterViewInit,
  ViewChild,
  ElementRef,
  Input,
  EventEmitter,
  Output,
  HostListener,
  NgZone,
  ChangeDetectorRef,
  ChangeDetectionStrategy,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { Subject, Subscription, takeUntil } from 'rxjs';
import { NotificationService } from '../../../services/notification.service'; // NotificationService;
import { EmployeeService } from '../../services/employee.service';
import { CompanyAdminService } from '../../services/company-admin.service';
import { AuthService } from '../../../services/auth.service';
import {
  Employee,
  EmployeeFilterData,
  EmployeeFilters,
  AttendanceWorkStatus,
} from '../../models/employee.models';
import { LocationOption } from '../../../shared/types';
import { SearchbarComponent } from '../../../shared/components/searchbar/searchbar.component';
import { ActionButtonComponent } from '../../../shared/components/action-button/action-button.component';
import { EmptyResultComponent } from '../../../shared/components/empty-result/empty-result.component';
import {
  LoadingTableShimmerComponent,
  ShimmerColumn,
} from '../../../shared/components/loading-table-shimmer/loading-table-shimmer.component';
import {
  PaginationBarComponent,
  PaginationData,
} from '../../../shared/components/pagination-bar/pagination-bar.component';
import {
  ConfirmationModalComponent,
  ConfirmationModalData,
} from '../../../shared/components/confirmation-modal/confirmation-modal.component';
import {
  FilterModalComponent,
  FilterCategory,
} from '../../../shared/components/filter-modal/filter-modal.component';
import {
  AddEmployeeModalComponent,
  AddEmployeeFormData,
} from '../add-employee-modal/add-employee-modal.component';
import { EmployeeProfileModalComponent } from '../employee-profile-modal/employee-profile-modal.component';
import { AttendanceRecordsModalComponent } from '../attendance-records-modal/attendance-records-modal.component';
import { TableUtils, DragScrollConfig } from '../../utils/table.utils';
import { RealtimeEventsService } from '../../../services/realtime-events.service';
import {
  AttendanceStatusChangedPayload,
  bindAttendanceStatusSseEvent,
} from '../../utils/attendance-status-realtime-sse.util';

/**
 * Displays and manages the employee list tab with search, pagination, filtering, and row actions such as view, edit, attendance, and delete.
 * @component
 * @selector app-employee-list-tab
 * @example
 *   <app-employee-list-tab [locationOptions]="sample_value1" [showAddModal]="sample_value2" (statsRefreshNeeded)="handler()" (addModalClosed)="handler()"></app-employee-list-tab>
 * @input {LocationOption[]} locationOptions - Available location options used to build the location filter categories.
 * @input {boolean} showAddModal - Controls whether the add-employee modal should be shown by the parent.
 * @output {EventEmitter<void>} statsRefreshNeeded - Emits when employee data changes so the parent can refresh summary/stat cards.
 * @output {EventEmitter<void>} addModalClosed - Emits when the add-employee modal is closed so the parent can update its modal state.
 * @output {EventEmitter<void>} addEmployeeRequested - Emits when empty-state "Add Employee" is clicked so the parent can open the modal.
 * @method load - Reloads the employee list; intended to be called by the parent when the tab becomes active.
 * @method openAddModal - Fallback hook for opening the add modal when triggered directly from a parent ViewChild reference.
 * @method onSearchChange - Updates the search term and debounces employee reloads.
 * @method onPageChange - Changes the current page and reloads employees.
 * @method onItemsPerPageChange - Updates page size, resets to page 1, and reloads employees.
 * @method openFilterModal - Initializes filter categories and opens the filter modal.
 * @method onFilterApply - Applies selected filters and reloads the employee list.
 * @method onCloseAddEmployeeModal - Notifies the parent that the add modal was closed.
 * @method onSaveEmployee - Handles successful employee creation, shows a success message, closes the modal, and reloads data.
 * @method editEmployee - Opens the edit modal for the selected employee.
 * @method onSaveEditEmployee - Closes the edit flow and refreshes the list after saving changes.
 * @method onViewEmployeeProfile - Opens the employee profile modal for the selected employee.
 * @method onViewAttendance - Opens the attendance records modal for the selected employee.
 * @method onDeleteEmployee - Opens the delete confirmation modal for the selected employee.
 * @method onEmployeeDeleteConfirm - Deletes the selected employee and refreshes the list on success.
 * @method toggleActionsDropdown - Opens or closes the row actions dropdown and positions it within the viewport.
 * @method trackByEmployeeId - Optimizes ngFor rendering by tracking rows with the employee ID.
 * @lifecycle OnInit - Loads employees during component initialization.
 * @lifecycle AfterViewInit - Reinitializes drag scrolling after the view is rendered.
 * @lifecycle OnDestroy - Cleans up subscriptions, timers, and table utilities to prevent memory leaks.
 */
@Component({
  selector: 'app-employee-list-tab',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    SearchbarComponent,
    ActionButtonComponent,
    EmptyResultComponent,
    LoadingTableShimmerComponent,
    PaginationBarComponent,
    ConfirmationModalComponent,
    FilterModalComponent,
    AddEmployeeModalComponent,
    EmployeeProfileModalComponent,
    AttendanceRecordsModalComponent,
  ],
  templateUrl: './employee-list-tab.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrls: ['./employee-list-tab.component.css'],
})
export class EmployeeListTabComponent implements OnInit, OnDestroy, AfterViewInit {
  @ViewChild('employeeTableScrollWrapper') employeeTableScrollWrapper!: ElementRef;

  @Input() locationOptions: LocationOption[] = [];
  @Input() showAddModal = false;

  /** Fires when employees change so the parent can refresh stats cards. */
  @Output() statsRefreshNeeded = new EventEmitter<void>();
  /** Fires to tell parent the add modal was closed (parent controls visibility). */
  @Output() addModalClosed = new EventEmitter<void>();
  /** Fires when empty-state Add Employee is clicked. */
  @Output() addEmployeeRequested = new EventEmitter<void>();

  private destroy$ = new Subject<void>();
  private employeeDragScrollConfig!: DragScrollConfig;
  private employeeSearchTimeout: ReturnType<typeof setTimeout> | null = null;
  private attendanceStatusSseSub: Subscription | null = null;

  employees: Employee[] = [];
  paginatedEmployees: Employee[] = [];
  filteredEmployees: Employee[] = [];
  employeeDisplayCache = new Map<string, any>();

  searchTerm = '';
  currentPage = 1;
  itemsPerPage = 10;
  totalItems = 0;
  totalPages = 1;
  loading = false;
  hasLoadedEmployeesOnce = false;

  /** Resolved from company admins API — not derived from the filtered list page. */
  currentUserIsCompanyOwner = false;

  activeActionsDropdown: string | null = null;
  selectedEmployeeForDropdown: Employee | null = null;

  appliedFilters: EmployeeFilterData = { locations: [], status: null };
  currentFilters: EmployeeFilterData = { locations: [], status: null };
  showFilterModal = false;
  filterCategories: FilterCategory[] = [];

  showEditEmployeeModal = false;
  employeeToEdit: Employee | null = null;

  showEmployeeProfileModal = false;
  selectedEmployee: Employee | null = null;

  showAttendanceRecordsModal = false;
  selectedEmployeeForAttendance: Employee | null = null;

  showEmployeeDeleteModal = false;
  isDeletingEmployee = false;
  employeeDeleteModalData: ConfirmationModalData = {
    title: 'Delete Employee?',
    description:
      "Are you sure you want to delete this employee? This action is permanent and can't be undone.",
    cancelText: 'Cancel',
    confirmText: 'Delete Employee',
    imageSrc: 'assets/images/empty_screen.png',
    imageAlt: 'Delete employee confirmation illustration',
    showTimeInfo: false,
    timeInfo: '',
    showCancelButton: true,
    showConfirmButton: true,
    isSuccessModal: false,
    isLoading: false,
  };
  employeeToDelete: Employee | null = null;

  showRemoveCoAdminModal = false;
  isRemovingCoAdmin = false;
  coAdminToRemove: Employee | null = null;
  removeCoAdminModalData: ConfirmationModalData = {
    title: 'Remove Company Admin?',
    description:
      'This will deactivate their login and remove them from the company. They will no longer be able to access this shop.',
    cancelText: 'Cancel',
    confirmText: 'Remove Admin',
    imageSrc: 'assets/images/empty_screen.png',
    imageAlt: 'Remove company admin confirmation illustration',
    showTimeInfo: false,
    timeInfo: '',
    showCancelButton: true,
    showConfirmButton: true,
    isSuccessModal: false,
    isLoading: false,
  };

  filters: EmployeeFilters = {
    status: { active: false, inactive: false },
    location: { mainBranch: false, downtownBranch: false },
  };

  shimmerColumns: ShimmerColumn[] = [
    { label: 'Employee', width: 'md' },
    { label: 'Role', width: 'sm' },
    { label: 'Contact', width: 'md' },
    { label: 'Location', width: 'sm' },
    { label: 'Status', width: 'sm' },
    { label: 'Permission', width: 'sm' },
    { label: 'Actions', width: 'xs' },
  ];

  /** Display label for users.role (Permission), distinct from Role (designation). */
  formatPermissionLabel(role: string | undefined | null): string {
    const normalized = role?.trim().toLowerCase();
    if (normalized === 'admin') {
      return 'Admin';
    }
    if (normalized === 'attendant') {
      return 'Attendant';
    }
    if (normalized === 'attendant_plus') {
      return 'Attendant plus';
    }
    if (normalized === 'staff') {
      return 'Staff';
    }
    return role?.trim() ? role : '—';
  }

  /** Pill theme class: admin black, attendant amber, attendant plus teal, staff violet. */
  permissionPillClass(role: string | undefined | null): string {
    const normalized = role?.trim().toLowerCase();
    if (normalized === 'admin') {
      return 'permission-admin';
    }
    if (normalized === 'attendant') {
      return 'permission-attendant';
    }
    if (normalized === 'attendant_plus') {
      return 'permission-attendant-plus';
    }
    if (normalized === 'staff') {
      return 'permission-staff';
    }
    return 'permission-any';
  }

  /** Shop-admin list row is not a manageable employees record. */
  isShopAdminRow(employee: Employee | null | undefined): boolean {
    return Boolean(employee?.is_shop_admin) || employee?.role === 'admin';
  }

  /** Current signed-in user is the company owner (shops.user_id). */
  isCurrentUserCompanyOwner(): boolean {
    return this.currentUserIsCompanyOwner;
  }

  canRemoveCompanyAdmin(employee: Employee | null | undefined): boolean {
    return (
      this.isShopAdminRow(employee) &&
      employee?.is_company_owner !== true &&
      this.isCurrentUserCompanyOwner()
    );
  }

  /** Show row actions for all employees and shop-admin rows (edit / remove). */
  showRowActions(employee: Employee | null | undefined): boolean {
    return !!employee;
  }

  get paginationData(): PaginationData {
    return {
      currentPage: this.currentPage,
      totalPages: this.totalPages,
      totalItems: this.totalItems,
      itemsPerPage: this.itemsPerPage,
    };
  }

  get hasActiveFilters(): boolean {
    return this.getActiveFilterCount() > 0;
  }

  constructor(
    public employeeService: EmployeeService,
    private companyAdminService: CompanyAdminService,
    private authService: AuthService,
    private messageService: NotificationService,
    private realtime: RealtimeEventsService,
    private ngZone: NgZone,
    // Angular 22 runs this app zoneless: state set inside an HTTP/SSE subscribe
    // callback never triggers a repaint on its own — ngZone.run() is a no-op here
    // (NoopNgZone). Found via automated testing: the employee table's own state held
    // 2 employees while the DOM showed "Total Employees 0" and an empty table.
    private cdr: ChangeDetectorRef
  ) {}

  ngOnInit(): void {
    this.resolveCurrentUserOwnership();
    this.loadEmployees();
    this.bindAttendanceStatusRealtime();
  }

  ngAfterViewInit(): void {
    this.reinitDragScroll();
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
    if (this.employeeSearchTimeout) clearTimeout(this.employeeSearchTimeout);
    this.attendanceStatusSseSub?.unsubscribe();
    this.attendanceStatusSseSub = null;
    TableUtils.cleanup();
  }

  /** Called by parent when this tab becomes active. */
  load(): void {
    this.loadEmployees();
  }

  /** Called by parent (title bar button) to open the add modal. */
  openAddModal(): void {
    // Handled via @Input() showAddModal from parent.
    // This is a fallback for direct ViewChild calls if needed.
  }

  @HostListener('document:click', ['$event'])
  onDocumentClick(event: Event): void {
    const target = event.target as HTMLElement;
    if (!target.closest('.actions')) this.activeActionsDropdown = null;
  }

  /**
   * Loads the employee list using the current pagination, search term, and applied filters, and updates related state.
   * @example
   * loadEmployees(true)
   * void
   * @param {boolean} updateStats - Whether to emit a stats refresh event after loading.
   * @returns {void} No return value.
   **/
  private loadEmployees(updateStats = false): void {
    this.loading = true;
    const filters = {
      locations: this.appliedFilters.locations,
      status: this.appliedFilters.status,
      includeAdmin: true,
      includeAttendanceStatus: true,
    };
    this.employeeService
      .loadEmployees(this.currentPage, this.itemsPerPage, this.searchTerm, filters)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: response => {
          const employees = response.employees || [];
          const totalItems = response.totalItems || 0;
          const totalPages = Math.max(1, response.totalPages || 1);

          // Stale page after delete/filter: empty rows but records remain — clamp and reload.
          if (employees.length === 0 && totalItems > 0 && this.currentPage > 1) {
            this.currentPage = totalPages;
            this.loadEmployees(updateStats);
            return;
          }

          this.employees = employees;
          this.totalItems = totalItems;
          this.currentPage = response.currentPage;
          this.totalPages = totalPages;
          this.itemsPerPage = response.itemsPerPage;
          this.updateEmployeeLists();
          this.hasLoadedEmployeesOnce = true;
          this.loading = false;
          if (updateStats) this.statsRefreshNeeded.emit();
          this.reinitDragScroll();
          this.cdr.markForCheck();
        },
        error: () => {
          if (this.employees.length === 0) {
            this.employees = [];
            this.totalItems = 0;
            this.totalPages = 1;
          }
          this.loading = false;
          this.updateEmployeeLists();
          if (updateStats) this.statsRefreshNeeded.emit();
          this.messageService.add({
            severity: 'error',
            summary: 'Error',
            detail: 'A technical error occurred. Please try again.',
          });
          this.cdr.markForCheck();
        },
      });
  }

  /**
   * Refreshes the employee list state and rebuilds the display cache for paginated employees.
   * @example
   * updateEmployeeLists()
   * void
   * @returns {void} No return value.
   **/
  private updateEmployeeLists(): void {
    this.filteredEmployees = this.employees;
    this.paginatedEmployees = this.filteredEmployees;
    this.employeeDisplayCache.clear();
    this.paginatedEmployees.forEach(emp => {
      this.employeeDisplayCache.set(emp.id, {
        fullName: this.employeeService.getEmployeeFullName(emp),
        initials: this.employeeService.getEmployeeInitials(emp.first_name, emp.last_name),
        employeeId: this.employeeService.getEmployeeId(emp),
        phoneNumber: this.employeeService.formatPhoneNumber(emp.phone_number),
        orderNumber: (emp.total_orders || 0).toString(),
      });
    });
  }

  getEmployeeDisplayData(employeeId: string, field: string): any {
    const cached = this.employeeDisplayCache.get(employeeId) as Record<string, any> | undefined;
    return cached ? cached[field] : '';
  }

  // Search
  onSearchChange(searchTerm: string): void {
    const wasSearching = this.searchTerm.trim().length > 0;
    this.searchTerm = searchTerm;
    this.currentPage = 1;
    if (wasSearching && !searchTerm.trim()) this.loading = true;
    if (this.employeeSearchTimeout) clearTimeout(this.employeeSearchTimeout);
    this.employeeSearchTimeout = setTimeout(() => this.loadEmployees(false), 300);
  }

  // Pagination
  onPageChange(page: number): void {
    if (page >= 1 && page <= this.totalPages) {
      this.currentPage = page;
      this.loadEmployees();
    }
  }

  onItemsPerPageChange(n: number): void {
    this.itemsPerPage = n;
    this.currentPage = 1;
    this.loadEmployees();
  }

  // Filter
  openFilterModal(): void {
    this.initFilterCategories();
    this.showFilterModal = true;
  }

  closeFilterModal(): void {
    this.showFilterModal = false;
  }

  /**
   * Initializes the filter categories for the employee list based on available locations and applied status filters.
   * @example
   * nitFilterCategories()
   * void
   * @returns {void} No return value.
   **/
  initFilterCategories(): void {
    this.filterCategories = [
      {
        id: 'location',
        name: 'Location',
        options: this.locationOptions.map(o => ({
          id: o.value,
          label: o.label,
          value: o.value,
          selected: this.appliedFilters.locations.includes(o.value),
        })),
      },
      {
        id: 'status',
        name: 'Status',
        options: [
          {
            id: 'active',
            label: 'Active',
            value: 'active',
            selected: this.appliedFilters.status === 'active',
          },
          {
            id: 'inactive',
            label: 'Inactive',
            value: 'inactive',
            selected: this.appliedFilters.status === 'inactive',
          },
        ],
      },
    ];
  }

  /**
   * Applies selected category filters to the employee list, resets pagination, and refreshes the results.
   * @example
   * nFilterApply(categories)
   * void
   * @param {FilterCategory[]} categories - Array of filter categories containing the selected filter options.
   * @returns {void} Does not return a value.
   **/
  onFilterApply(categories: FilterCategory[]): void {
    const locCat = categories.find(c => c.id === 'location');
    const statusCat = categories.find(c => c.id === 'status');
    const filterData: EmployeeFilterData = {
      locations: locCat?.options.filter(o => o.selected).map(o => o.value) || [],
      status: (statusCat?.options.find(o => o.selected)?.value || null) as
        'active' | 'inactive' | null,
    };
    this.appliedFilters = { ...filterData };
    this.currentFilters = filterData;
    this.currentPage = 1;
    this.loadEmployees();
    this.closeFilterModal();
  }

  getActiveFilterCount(): number {
    return this.appliedFilters.locations.length + (this.appliedFilters.status ? 1 : 0);
  }

  // Add Employee (driven by parent Input)
  onCloseAddEmployeeModal(): void {
    this.addModalClosed.emit();
  }

  onEmptyAddEmployeeClick(): void {
    this.addEmployeeRequested.emit();
  }

  onSaveEmployee(formData: AddEmployeeFormData): void {
    const isAdmin = formData?.role === 'admin';
    this.messageService.add({
      severity: 'success',
      summary: 'Success',
      detail: isAdmin ? 'Admin Added!' : 'Employee Added!',
    });
    this.addModalClosed.emit();
    this.loadEmployees(true);
  }

  // Edit Employee
  editEmployee(employee: Employee): void {
    this.employeeToEdit = employee;
    this.showEditEmployeeModal = true;
  }

  onCloseEditEmployeeModal(): void {
    this.showEditEmployeeModal = false;
    this.employeeToEdit = null;
  }

  onSaveEditEmployee(_employeeData: AddEmployeeFormData): void {
    if (this.employeeToEdit) {
      this.showEditEmployeeModal = false;
      this.employeeToEdit = null;
      this.loadEmployees(true);
    }
  }

  // Profile
  onViewEmployeeProfile(employee: Employee): void {
    this.selectedEmployee = employee;
    this.showEmployeeProfileModal = true;
  }

  onCloseEmployeeProfileModal(): void {
    this.showEmployeeProfileModal = false;
    this.selectedEmployee = null;
  }

  // Attendance
  onViewAttendance(employee: Employee): void {
    if (this.isShopAdminRow(employee)) {
      return;
    }
    this.selectedEmployeeForAttendance = employee;
    this.showAttendanceRecordsModal = true;
  }

  onCloseAttendanceRecordsModal(): void {
    this.showAttendanceRecordsModal = false;
    this.selectedEmployeeForAttendance = null;
  }

  // Delete Employee
  onDeleteEmployee(employee: Employee): void {
    if (this.isShopAdminRow(employee)) {
      return;
    }
    this.activeActionsDropdown = null;
    this.selectedEmployeeForDropdown = null;
    this.employeeToDelete = employee;
    this.showEmployeeDeleteModal = true;
  }

  onEmployeeDeleteCancel(): void {
    if (this.isDeletingEmployee) return;
    this.showEmployeeDeleteModal = false;
    this.employeeToDelete = null;
    this.employeeDeleteModalData.isLoading = false;
  }

  /**
   * Confirms deletion of the selected employee and updates the UI based on the result.
   * @example
   * nEmployeeDeleteConfirm()
   * void
   * @returns {void} No return value.
   **/
  onEmployeeDeleteConfirm(): void {
    if (this.isDeletingEmployee || !this.employeeToDelete) return;
    this.isDeletingEmployee = true;
    this.employeeDeleteModalData.isLoading = true;
    this.employeeService.deleteEmployee(this.employeeToDelete.id).subscribe({
      next: (response: any) => {
        this.isDeletingEmployee = false;
        this.employeeDeleteModalData.isLoading = false;
        if (response.status && response.status_code === 200) {
          this.messageService.add({
            severity: 'success',
            summary: 'Success',
            detail: 'Employee deleted successfully!',
          });
          this.showEmployeeDeleteModal = false;
          this.employeeToDelete = null;
          this.loadEmployees(true);
        } else {
          this.messageService.add({
            severity: 'error',
            summary: 'Error',
            detail: response.message || 'Failed to delete employee',
          });
        }
        this.cdr.markForCheck();
      },
      error: () => {
        this.isDeletingEmployee = false;
        this.employeeDeleteModalData.isLoading = false;
        this.messageService.add({
          severity: 'error',
          summary: 'Error',
          detail: 'Failed to delete employee. Please try again.',
        });
        this.cdr.markForCheck();
      },
    });
  }

  onRemoveCompanyAdmin(employee: Employee): void {
    if (!this.canRemoveCompanyAdmin(employee)) {
      return;
    }
    this.activeActionsDropdown = null;
    this.selectedEmployeeForDropdown = null;
    this.coAdminToRemove = employee;
    this.showRemoveCoAdminModal = true;
  }

  onRemoveCoAdminCancel(): void {
    if (this.isRemovingCoAdmin) return;
    this.showRemoveCoAdminModal = false;
    this.coAdminToRemove = null;
    this.removeCoAdminModalData.isLoading = false;
  }

  onRemoveCoAdminConfirm(): void {
    if (this.isRemovingCoAdmin || !this.coAdminToRemove) return;
    this.isRemovingCoAdmin = true;
    this.removeCoAdminModalData.isLoading = true;
    this.companyAdminService.removeCompanyAdmin(this.coAdminToRemove.id).subscribe({
      next: response => {
        this.isRemovingCoAdmin = false;
        this.removeCoAdminModalData.isLoading = false;
        if (response?.status) {
          this.messageService.add({
            severity: 'success',
            summary: 'Success',
            detail: 'Company admin removed successfully!',
          });
          this.showRemoveCoAdminModal = false;
          this.coAdminToRemove = null;
          this.loadEmployees(true);
          this.statsRefreshNeeded.emit();
        } else {
          this.messageService.add({
            severity: 'error',
            summary: 'Error',
            detail: response?.message || 'Failed to remove company admin',
          });
        }
        this.cdr.markForCheck();
      },
      error: err => {
        this.isRemovingCoAdmin = false;
        this.removeCoAdminModalData.isLoading = false;
        this.messageService.add({
          severity: 'error',
          summary: 'Error',
          detail: err?.error?.message || 'Failed to remove company admin. Please try again.',
        });
        this.cdr.markForCheck();
      },
    });
  }

  // Actions Dropdown
  toggleActionsDropdown(event: Event, employeeId: string): void {
    if (this.activeActionsDropdown === employeeId) {
      this.activeActionsDropdown = null;
      this.selectedEmployeeForDropdown = null;
    } else {
      this.activeActionsDropdown = employeeId;
      this.selectedEmployeeForDropdown = this.employees.find(e => e.id === employeeId) || null;
      this.positionDropdown(event, '.actions-dropdown-menu');
    }
  }

  onEditEmployee(employee: Employee): void {
    this.activeActionsDropdown = null;
    this.selectedEmployeeForDropdown = null;
    this.editEmployee(employee);
  }

  /**
   * Resolve ownership from the company-admins list (not the filtered employees page).
   */
  private resolveCurrentUserOwnership(): void {
    this.companyAdminService
      .listCompanyAdmins()
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: response => {
          const currentUserId = this.authService.user()?.id;
          const admins = Array.isArray(response?.data) ? response.data : [];
          if (!currentUserId) {
            this.currentUserIsCompanyOwner = false;
            this.cdr.markForCheck();
            return;
          }
          const self = admins.find(admin => String(admin.id) === String(currentUserId));
          this.currentUserIsCompanyOwner = self?.is_company_owner === true;
          this.cdr.markForCheck();
        },
        error: () => {
          this.currentUserIsCompanyOwner = false;
          this.cdr.markForCheck();
        },
      });
  }

  trackByEmployeeId(_: number, employee: Employee): string {
    return employee.id;
  }

  /**
   * Get the attendance status for an employee (checked_in, checked_out, or offline).
   * Inactive employees have no live attendance badge (show em dash in the template).
   * Checked-in staff who are idle (presence_status offline) show Offline, not In.
   * @example
   * getAttendanceStatus(employee)
   * 'checked_in'
   * @param {Employee} employee - The employee object to check.
   * @returns {AttendanceWorkStatus | null} The attendance work status, or null when inactive.
   **/
  getAttendanceStatus(employee: Employee): AttendanceWorkStatus | null {
    if (!employee.emp_status) return null;
    if (employee.work_status === 'checked_out') return 'checked_out';
    if (employee.work_status === 'checked_in' || employee.work_status === 'on_break') {
      return employee.presence_status === 'offline' ? 'offline' : 'checked_in';
    }
    return 'offline';
  }

  /**
   * Get the display label for an attendance status.
   * @example
   * getAttendanceStatusLabel('checked_in')
   * 'In'
   * @param {AttendanceWorkStatus} status - The attendance work status.
   * @returns {string} The display label.
   **/
  getAttendanceStatusLabel(status: AttendanceWorkStatus): string {
    return status === 'checked_in' ? 'In' : status === 'checked_out' ? 'Out' : 'Offline';
  }

  /**
   * Get the CSS class for an attendance status badge.
   * @example
   * getAttendanceStatusClass('checked_in')
   * 'attendance-checked_in'
   * @param {AttendanceWorkStatus} status - The attendance work status.
   * @returns {string} The CSS class string.
   **/
  getAttendanceStatusClass(status: AttendanceWorkStatus): string {
    return `attendance-${status}`;
  }

  /**
   * Live Attendance column updates via SSE (check-in / check-out / presence).
   * Patches matching rows in place and refreshes stats cards.
   */
  private bindAttendanceStatusRealtime(): void {
    if (this.attendanceStatusSseSub) {
      return;
    }
    this.attendanceStatusSseSub = bindAttendanceStatusSseEvent(this.realtime, this.ngZone, {
      onAttendanceStatusChanged: payload => this.applyAttendanceStatusUpdate(payload),
    });
  }

  /**
   * Apply an attendance_status_changed SSE payload to the current page of employees.
   */
  private applyAttendanceStatusUpdate(payload: AttendanceStatusChangedPayload): void {
    const employeeId = payload.employee_id;

    const patchList = (list: Employee[]): { list: Employee[]; updated: boolean } => {
      let updated = false;
      const next = list.map(emp => {
        if (emp.id !== employeeId) {
          return emp;
        }
        updated = true;
        const patched: Employee = { ...emp };
        if (payload.work_status) {
          patched.work_status = payload.work_status as Employee['work_status'];
        }
        if (payload.presence === 'online' || payload.presence === 'offline') {
          patched.presence_status = payload.presence;
          if (payload.presence === 'online') {
            patched.last_seen = new Date().toISOString();
          }
        } else if (payload.work_status === 'checked_in' || payload.work_status === 'on_break') {
          patched.presence_status = 'online';
          patched.last_seen = new Date().toISOString();
        } else if (payload.work_status === 'checked_out') {
          patched.presence_status = null;
        }
        return patched;
      });
      return { list: next, updated };
    };

    const employeesResult = patchList(this.employees);
    const paginatedResult = patchList(this.paginatedEmployees);
    const filteredResult = patchList(this.filteredEmployees);

    if (!employeesResult.updated && !paginatedResult.updated && !filteredResult.updated) {
      return;
    }

    this.employees = employeesResult.list;
    this.paginatedEmployees = paginatedResult.list;
    this.filteredEmployees = filteredResult.list;
    this.statsRefreshNeeded.emit();
    this.cdr.markForCheck();
  }

  /**
   * Positions a dropdown menu near the triggering button and adjusts placement to fit within the viewport.
   * @example
   * positionDropdown(event, '.dropdown-menu')
   * void
   * @param {Event} event - The click or interaction event used to locate the triggering button.
   * @param {string} selector - CSS selector used to find the dropdown element to position.
   * @returns {void} Does not return a value.
   **/
  private positionDropdown(event: Event, selector: string): void {
    requestAnimationFrame(() => {
      const button = (event.target as HTMLElement).closest('.actions-button') as HTMLElement;
      const dropdown = document.querySelector(selector) as HTMLElement;
      if (button && dropdown) {
        const rect = button.getBoundingClientRect();
        const dropdownHeight = dropdown.offsetHeight || 88;
        const viewportHeight = window.innerHeight;
        dropdown.style.position = 'fixed';
        dropdown.style.zIndex = '9999';
        if (viewportHeight - rect.bottom >= dropdownHeight + 10) {
          dropdown.style.top = `${rect.bottom + 5}px`;
          dropdown.style.bottom = 'auto';
        } else {
          dropdown.style.top = 'auto';
          dropdown.style.bottom = `${viewportHeight - rect.top + 5}px`;
        }
        dropdown.style.left = `${rect.right - dropdown.offsetWidth}px`;
      }
    });
  }

  private reinitDragScroll(): void {
    setTimeout(() => {
      if (this.employeeTableScrollWrapper) {
        this.employeeDragScrollConfig = TableUtils.createDragScrollConfig(
          this.employeeTableScrollWrapper
        );
        TableUtils.initializeDragScrolling(this.employeeDragScrollConfig);
      }
    }, 100);
  }
}
